import { Image as CrystallizeImage } from '@crystallize/react-image'
import Image from 'next/image'

import type { CartItem as CartItemType } from '@/service-api/types.generated'
import { isFreightProduct } from '@/lib/freight'
import { getWasteIconBySku } from '@/app/[locale]/(common)/(waste-icons)/waste-icons'

import { useBasket } from './index'
import { Button } from '@/app/[locale]/(ui)/button'
import { formatAmountToNOK } from '@/lib/utils'
import { LoaderCircle, Minus, Plus, X } from 'lucide-react'
import { useTranslations } from 'next-intl'

export const BasketItem = ({ item, discount = 0 }: { item: CartItemType; discount?: number }) => {
  const t = useTranslations()
  const basket = useBasket()
  const sizePath = (item as any).sizePath as string | undefined
  const bagSizeImageByPath: Record<string, string> = {
    tralle: '/waste-bag-size/tralle_isekk.svg',
    standard: '/waste-bag-size/standard_isekk.svg',
    stor: '/waste-bag-size/large_isekk.svg',
    xxl: '/waste-bag-size/XXL_isekk.svg',
  }
  const wasteIcon = getWasteIconBySku(item.sku || '')
  const WasteIcon = wasteIcon?.component
  const bagSizeImage = sizePath ? bagSizeImageByPath[sizePath] : undefined
  const basketImage = bagSizeImage || item.productImage
  const thumbnailImage = wasteIcon?.url || item.images?.[0]?.url
  const isItemUpdating =
    basket?.status === 'server-basket-is-stale' &&
    basket?.attentionCartItem?.sku === item.sku &&
    basket?.attentionCartItem?.sizePath === (item as any).sizePath
  const { isCompany } = basket

  return (
    <div className="relative w-full bg-white">
      <div className="flex w-full gap-5">
        <div className="flex flex-col">
          {WasteIcon ? (
            <div className="h-[25px] w-[25px] overflow-hidden rounded border border-[#DFDFDF]">
              <WasteIcon className="h-full w-full" />
            </div>
          ) : (
            <CrystallizeImage
              url={thumbnailImage}
              style={{
                alignItems: 'flex-start',
                borderRadius: '4px',
                border: 'solid 1px #DFDFDF',
                width: '20px',
                height: '20px',
              }}
            />
          )}
          <div className="h-[64px] w-[64px] overflow-hidden rounded sm:h-[80px] sm:w-[80px]">
            {basketImage ? (
              <div className="relative h-full w-full">
                <Image
                  alt=""
                  src={basketImage}
                  fill
                  sizes="(min-width: 640px) 80px, 64px"
                  className="object-contain p-1"
                />
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gray-100">
                <span className="text-xs text-gray-600">{t('No_image')}</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-grow flex-col items-start justify-between self-stretch pr-5">
          <div className="font-bold">{item.productName}</div>
          <div>{item.name}</div>
          <div className="mt-2 flex items-center rounded-md bg-gray-100">
            {isFreightProduct(item) ? (
              <div className="px-2.5">{item.quantity}</div>
            ) : (
              <>
                <Button
                  variant="tertiary"
                  shape="square"
                  name="decrement-item"
                  onClick={() => basket?.actions.decrementItem(item)}
                  disabled={isItemUpdating || item.quantity <= 1}
                >
                  <Minus size={20} />
                </Button>
                <div className="inline-block px-1.5 text-center">{item.quantity}</div>
                <Button
                  variant="tertiary"
                  shape="square"
                  name="increment-item"
                  onClick={() => basket?.actions.incrementItem(item)}
                  disabled={isItemUpdating}
                >
                  <Plus size={20} />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col items-end justify-between self-stretch">
        {isFreightProduct(item) ? (
          <div />
        ) : (
          <Button
            variant="tertiary"
            shape="square"
            name="remove-item"
            className="absolute right-0 top-0"
            aria-label={t('Remove')}
            onClick={() => basket?.actions.removeItem(item)}
          >
            <X size={20} />
          </Button>
        )}
        <div className="flex flex-col items-stretch justify-end">
          {!isCompany && (
            <>
              {discount > 0 && (
                <>
                  {basket?.status === 'server-basket-is-stale' ? (
                    <div className="animate-pulse opacity-20">...</div>
                  ) : (
                    <div className="line-through opacity-20">
                      {formatAmountToNOK((item.quantity * (item.price?.gross ?? 0)) / (1 - discount / 100))}
                    </div>
                  )}
                </>
              )}
              {/* Show loading indicator for freight items when basket is updating */}
              {isFreightProduct(item) && basket?.status === 'server-basket-is-stale' ? (
                <LoaderCircle className="animate-spin" />
              ) : /* Show price only if it exists and is greater than 0 */
              item.price?.gross && item.price.gross > 0 ? (
                <div>{formatAmountToNOK(item.quantity * item.price.gross)}</div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
